import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    users: Array
  }

  connect() {
    this.setupAutocomplete()
  }

  disconnect() {
    this.cleanup()
  }

  setupAutocomplete() {
    this.textarea = this.element
    this.suggestionContainer = null
    this.currentSuggestions = []
    this.selectedIndex = -1

    // Bind event listeners
    this.textarea.addEventListener('input', this.handleInput.bind(this))
    this.textarea.addEventListener('keydown', this.handleKeydown.bind(this))
    this.textarea.addEventListener('blur', this.handleBlur.bind(this))
  }

  handleInput(event) {
    const text = this.textarea.value
    const cursorPosition = this.textarea.selectionStart

    // Find the current word and check if it starts with @
    const beforeCursor = text.substring(0, cursorPosition)
    const words = beforeCursor.split(/\s/)
    const currentWord = words[words.length - 1]

    if (currentWord.startsWith('@') && currentWord.length > 1) {
      const query = currentWord.substring(1).toLowerCase()
      this.showSuggestions(query, cursorPosition)
    } else {
      this.hideSuggestions()
    }
  }

  handleKeydown(event) {
    if (!this.suggestionContainer || this.currentSuggestions.length === 0) {
      return
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.currentSuggestions.length - 1)
        this.updateSelection()
        break

      case 'ArrowUp':
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1)
        this.updateSelection()
        break

      case 'Enter':
      case 'Tab':
        if (this.selectedIndex >= 0) {
          event.preventDefault()
          this.selectSuggestion(this.currentSuggestions[this.selectedIndex])
        }
        break

      case 'Escape':
        event.preventDefault()
        this.hideSuggestions()
        break
    }
  }

  handleBlur(event) {
    // Delay hiding to allow clicking on suggestions
    setTimeout(() => {
      this.hideSuggestions()
    }, 200)
  }

  showSuggestions(query, cursorPosition) {
    // Filter users based on query
    this.currentSuggestions = this.usersValue.filter(user =>
      user.name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query)
    ).slice(0, 5) // Limit to 5 suggestions

    if (this.currentSuggestions.length === 0) {
      this.hideSuggestions()
      return
    }

    // Create or update suggestion container
    if (!this.suggestionContainer) {
      this.suggestionContainer = document.createElement('div')
      this.suggestionContainer.className = 'overflow-y-auto absolute z-[1000] max-h-40 bg-white rounded-md border border-gray-300 shadow-lg mentions-suggestions'
      document.body.appendChild(this.suggestionContainer)
    }

    // Position the container
    this.positionSuggestions(cursorPosition)

    // Populate suggestions
    this.suggestionContainer.innerHTML = ''
    this.currentSuggestions.forEach((user, index) => {
      const suggestion = this.createSuggestionElement(user, index)
      this.suggestionContainer.appendChild(suggestion)
    })

    this.selectedIndex = -1
    this.suggestionContainer.style.display = 'block'
  }

  hideSuggestions() {
    if (this.suggestionContainer) {
      this.suggestionContainer.style.display = 'none'
    }
    this.currentSuggestions = []
    this.selectedIndex = -1
  }

  createSuggestionElement(user, index) {
    const element = document.createElement('div')
    element.className = 'flex gap-2 items-center p-2 cursor-pointer hover:bg-gray-100'
    element.innerHTML = `
      <div class="flex justify-center items-center w-6 h-6 bg-blue-100 rounded-full">
        <span class="text-xs font-medium text-blue-600">${user.name.charAt(0).toUpperCase()}</span>
      </div>
      <div class="flex-1">
        <div class="text-sm font-medium text-gray-900">${user.name}</div>
        <div class="text-xs text-gray-500">${user.email}</div>
      </div>
    `

    element.addEventListener('click', () => {
      this.selectSuggestion(user)
    })

    element.addEventListener('mouseenter', () => {
      this.selectedIndex = index
      this.updateSelection()
    })

    return element
  }

  updateSelection() {
    const suggestions = this.suggestionContainer.querySelectorAll('.p-2')
    suggestions.forEach((el, index) => {
      if (index === this.selectedIndex) {
        el.classList.add('bg-blue-100')
        el.classList.remove('hover:bg-gray-100')
      } else {
        el.classList.remove('bg-blue-100')
        el.classList.add('hover:bg-gray-100')
      }
    })
  }

  selectSuggestion(user) {
    const text = this.textarea.value
    const cursorPosition = this.textarea.selectionStart

    // Find the start of the current @mention
    const beforeCursor = text.substring(0, cursorPosition)
    const words = beforeCursor.split(/\s/)
    const currentWordStart = beforeCursor.lastIndexOf(words[words.length - 1])

    // Replace the current @mention with the selected user
    const newText = text.substring(0, currentWordStart) +
                   `@${user.name} ` +
                   text.substring(cursorPosition)

    this.textarea.value = newText

    // Position cursor after the inserted mention
    const newCursorPosition = currentWordStart + user.name.length + 2
    this.textarea.setSelectionRange(newCursorPosition, newCursorPosition)

    this.hideSuggestions()
    this.textarea.focus()

    // Trigger input event to notify other listeners
    this.textarea.dispatchEvent(new Event('input', { bubbles: true }))
  }

  positionSuggestions(cursorPosition) {
    // Create a temporary span to measure text
    const span = document.createElement('span')
    span.style.cssText = `
      position: absolute;
      visibility: hidden;
      white-space: pre-wrap;
      font-family: ${getComputedStyle(this.textarea).fontFamily};
      font-size: ${getComputedStyle(this.textarea).fontSize};
      line-height: ${getComputedStyle(this.textarea).lineHeight};
      padding: ${getComputedStyle(this.textarea).padding};
    `

    span.textContent = this.textarea.value.substring(0, cursorPosition)
    document.body.appendChild(span)

    const textareaRect = this.textarea.getBoundingClientRect()
    const spanRect = span.getBoundingClientRect()

    this.suggestionContainer.style.left = `${textareaRect.left + spanRect.width}px`
    this.suggestionContainer.style.top = `${textareaRect.bottom + 5}px`
    this.suggestionContainer.style.minWidth = '200px'

    document.body.removeChild(span)
  }

  cleanup() {
    if (this.suggestionContainer) {
      this.suggestionContainer.remove()
      this.suggestionContainer = null
    }
  }
}
